import ContentLayout from '../../layout/ContentLayout';
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tabs, Button, Dropdown } from 'antd';
import styles from '../../styles/subscriptions.module.scss';
import Search from 'antd/es/transfer/search';
import TableSection from '../../components/tables/TableSection';
import {fetchSubscriptions, cancelSubscriptionForUser} from '../../api/subscription-api';
import DeleteModal from '../../components/modals/DeleteModal';
import dayjs from 'dayjs';
import { formatPriceWithCurrency } from '../../utils/formatPrice';
import {toast} from "react-toastify";
import Spinner from '../../components/elements/Spinner/Spinner';
import PackageSubscriptionsTable from '../../components/tables/PackageSubscriptionsTable';

const tabItems = [
  {
    key: 'pretplate',
    label: 'Pretplaćeni korisnici',
  },
  {
    key: 'paketi',
    label: 'Pretplate na pakete',
  },
];

const SubscriptionsSystem = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get('tab') || 'pretplate';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [filter, setFilter] = useState({
    status: null,
  });
  const [subscriptions, setSubscriptions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const subscriptionsColumns = [
    {
      key: 'fullName',
      label: 'Korisnik',
      // render: (_, row) => {
      //   const full_name = `${row.firstName || ''} ${row.lastName || ''}`;
      //   return full_name;
      // },
    },
    {
      key: 'email',
      label: 'Email',
      // render: (val) => userTypeTranslations[val] || val,
    },
    {
      key: 'status',
      label: 'Status',
      render: (val) => {
        const statusMap = {
          active:          { label: 'Aktivan',                    bg: '#e6f4ea', color: '#1e7e34' },
          deactivated:     { label: 'Neaktivan',                  bg: '#fdecea', color: '#c0392b' },
          pending_payment: { label: 'Na čekanju - plaćanje',      bg: '#fff3e0', color: '#e65100' },
          pending_setup:   { label: 'Na čekanju - podešavanje',   bg: '#f0f0f0', color: '#555555' },
        };
        const key = val ? val.toLowerCase() : '';
        const { label, bg, color } = statusMap[key] ?? { label: val ?? '-', bg: '#f0f0f0', color: '#555555' };
        return (
            <span style={{
              display: 'inline-block',
              padding: '2px 10px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              backgroundColor: bg,
              color,
            }}>
        {label}
      </span>
        );
      },
    },
    {
      key: 'nextBillingDate',
      label: 'Datum naredne pretplate',
      render: (val, row) => {
        const showDate = row.status === 'ACTIVE' || row.status === 'PENDING_PAYMENT';
        if (!showDate) {
          return <span>-</span>;
        }
        const date = dayjs(val).format('DD.MM.YYYY.');
        return <span>{date}</span>;
      },
    },
    {
      key: 'total',
      label: 'Iznos pretplate',
      render: (val) => <span>{formatPriceWithCurrency(val ?? 0)}</span>,
    },
    // {
    //   // TODO:
    //   key: '',
    //   label: 'Referral link',
    //   // render: (_, row) => {
    //   //   return (
    //   //     <span style={{ color: '#005844' }}>{row.referralCode || '-'}</span>
    //   //   );
    //   // },
    // },
  ];

  const handleTabChange = (key) => {
    setActiveTab(key);
    const params = new URLSearchParams(location.search);
    params.set('tab', key);
    navigate({ search: params.toString() }, { replace: true });
  };

  const subscriptionsFilterOptions = [
    { label: 'Aktivan', value: 'ACTIVE' },
    { label: 'Neaktivan', value: 'DEACTIVATED' },
    { label: 'Na čekanju - plaćanje', value: 'PENDING_PAYMENT' },
    { label: 'Na čekanju - podešavanje', value: 'PENDING_SETUP' },
  ];

  const onSearchSubscriptions = (value) => {
    setSearch(value);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      // Reset to page 1 when search changes
      if (search !== debouncedSearch) {
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [search, debouncedSearch]);

  const loadSubscriptions = useCallback(async () => {
    const { data, success, message } = await fetchSubscriptions({
      page: currentPage,
      limit: 10,
      filter,
      search: debouncedSearch,
    });

    if (success) {
      setSubscriptions(data.subscriptions);
      setTotalPages(data.pagination.totalPages);
    } else {
      console.error(message || 'Error while fetching subscriptions.');
    }
    setLoading(false);
  }, [filter, debouncedSearch, currentPage, activeTab]);

  useEffect(() => {
    loadSubscriptions();
    // eslint-disable-next-line
  }, [filter, debouncedSearch, currentPage, activeTab]);

  const handleConfirmCancel = useCallback(async () => {
    setCancelling(true);
    const userId = cancelTarget.userId;
    const result = await cancelSubscriptionForUser(userId);
    setCancelling(false);

    if (result.success) {
      setCancelTarget(null);
      loadSubscriptions();
    } else {
      toast.error(result.message);
    }
  }, [cancelTarget, loadSubscriptions]);

  const renderTable = (
    state,
    emptyState = {
      icon: '/assets/icons/no-users.svg',
      title: 'Trenutno nema podataka',
    },
    showFilter = true
  ) => {
    return (
      <TableSection
        columns={state.columns}
        actions={[
          {
            key: 'header',
            label: 'Opcije',
            disabled: true,
            className: 'dropdown-header',
          },
          {
            key: 'view',
            label: 'Vidi detalje',
            icon: <img src="/assets/icons/view-icon.svg" alt="View" />,
            onClick: (order) => navigate(`/pretplatnicki-sistem/${order._id}`),
          },
          {
            key: 'cancel',
            label: 'Otkaži pretplatu',
            icon: <img src="/assets/icons/delete.svg" alt="Cancel" />,
            onClick: (order) => setCancelTarget(order),
            visible: (order) => order.status === 'ACTIVE' || order.status === 'PENDING_PAYMENT',
            danger: true,
            className: 'delete',
          },
        ]}
        data={state.data}
        emptyState={emptyState}
        showPagination={state.data?.length > 0}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        searchPlaceholder="Pretraži"
      />
    );
  };

  return (
    <>
      <ContentLayout>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabItems}
          className="medics_tab"
        />
        {activeTab === 'pretplate' && (
          <>
            <div className={`${styles.wrapper}`}>
              <div className={styles.filters}>
                <Dropdown
                  menu={{
                    items: subscriptionsFilterOptions.map((option) => ({
                      key: option.value,
                      label: option.label,
                      onClick: () => {
                        setFilter((prev) => {
                          const updated = {
                            ...prev,
                            status: option.value,
                          };
                          return updated;
                        });
                      },
                    })),
                  }}
                  trigger={['click']}
                  placement="bottomLeft"
                  className={'btn-filter-container'}
                >
                  <Button
                    className={`btn-tertiary btn-filter ${filter.status ? 'active' : ''}`}
                  >
                    {filter.status
                      ? `Status: ${
                          subscriptionsFilterOptions.find(
                            (option) => option.value === filter.status
                          )?.label
                        }`
                      : 'Status'}
                    {filter.status && (
                      <img
                        src="/assets/icons/x-gray.svg"
                        title="Ukloni filter status"
                        style={{
                          width: '10px',
                          height: '10px',
                          marginRight: '5px',
                          cursor: 'pointer',
                        }}
                        alt="Ukloni filter"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFilter((prev) => {
                            const updated = { ...prev, status: null };
                            return updated;
                          });
                        }}
                      />
                    )}
                  </Button>
                </Dropdown>
                <div>
                  <Search
                    placeholder={'Pretraži'}
                    className="search-input"
                    allowClear
                    enterButton
                    onChange={(e) => onSearchSubscriptions(e.target.value)}
                    width="auto"
                  />
                </div>
              </div>
              <>
                {loading ? (
                  <Spinner size="lg" />
                ) : renderTable(
                  {
                    data: subscriptions,
                    columns: subscriptionsColumns,
                  },
                  {
                    icon: '/assets/icons/no-users.svg',
                    title: 'Nema pretplata',
                    description: 'Napravite korisnika',
                  }
                )}
              </>
            </div>
          </>
        )}
        {activeTab === 'paketi' && <PackageSubscriptionsTable />}
      </ContentLayout>
      <DeleteModal
        visible={!!cancelTarget}
        onDelete={handleConfirmCancel}
        onCancel={() => setCancelTarget(null)}
        loading={cancelling}
        title="Otkazivanje pretplate"
        message={`Da li ste sigurni da želite da otkažete pretplatu za korisnika ${cancelTarget?.fullName}?`}
        buttonText="Otkaži pretplatu"
        cancelText="Odustani"
      />
    </>
  );
};
export default SubscriptionsSystem;
